import {
	type AccountInfo,
	type Configuration,
	InteractionRequiredAuthError,
	PublicClientApplication
} from '@azure/msal-browser';

import { ADO_SCOPES, AZURE_AUTHORITY, AZURE_TOKEN_CACHE_LOCATION } from '../consts/AzureConstants';
import { AzureLogger } from '../types/AzureTicketCreatorConfig';

export interface AzureAuthClient {
	/** True once the user has signed in with a Microsoft (Entra) account. */
	isAzureConnected(): Promise<boolean>;
	/** Interactive sign-in via popup (keeps the user on the current export modal). */
	connectToAzure(): Promise<boolean>;
	/** Returns true when signed in; triggers interactive sign-in if not yet connected. */
	ensureAzureConnected(): Promise<boolean>;
	/** Returns a bearer token ADO accepts. Silent first, interactive popup as fallback. */
	acquireAzureToken(): Promise<string>;
}

interface AzureAuthClientConfig {
	clientId: string;
	redirectUri: string;
	logger: AzureLogger;
}

/**
 * Creates an MSAL-backed auth client. Each client owns its own PublicClientApplication built
 * from the given config — there is no module-level state, so it's configured per provider/test.
 */
export const createAzureAuthClient = ({
	clientId,
	redirectUri,
	logger
}: AzureAuthClientConfig): AzureAuthClient => {
	const msalConfig: Configuration = {
		auth: {
			clientId,
			authority: AZURE_AUTHORITY,
			redirectUri
		},
		cache: {
			cacheLocation: AZURE_TOKEN_CACHE_LOCATION
		}
	};
	const msalInstance = new PublicClientApplication(msalConfig);

	let initPromise: Promise<void> | null = null;
	const ensureInitialized = (): Promise<void> => {
		if (!initPromise) {
			// Reset on failure so a transient init error doesn't break auth permanently.
			initPromise = msalInstance.initialize().catch((error) => {
				initPromise = null;
				throw error;
			});
		}
		return initPromise;
	};

	const getActiveAccount = (): AccountInfo | null =>
		msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0] ?? null;

	const isAzureConnected = async (): Promise<boolean> => {
		await ensureInitialized();
		return !!getActiveAccount();
	};

	const connectToAzure = async (): Promise<boolean> => {
		await ensureInitialized();
		const result = await msalInstance.loginPopup({ scopes: ADO_SCOPES });
		if (result?.account) {
			msalInstance.setActiveAccount(result.account);
		}
		return !!result?.account;
	};

	const ensureAzureConnected = async (): Promise<boolean> => {
		if (await isAzureConnected()) {
			return true;
		}
		return connectToAzure();
	};

	const acquireAzureToken = async (): Promise<string> => {
		await ensureInitialized();
		const account = getActiveAccount();
		if (!account) {
			throw new Error('Unauthorized');
		}
		try {
			const { accessToken } = await msalInstance.acquireTokenSilent({
				scopes: ADO_SCOPES,
				account
			});
			return accessToken;
		} catch (error) {
			if (error instanceof InteractionRequiredAuthError) {
				const { accessToken } = await msalInstance.acquireTokenPopup({
					scopes: ADO_SCOPES,
					account
				});
				return accessToken;
			}
			logger.error('acquireAzureToken failed', error);
			throw error;
		}
	};

	return { isAzureConnected, connectToAzure, ensureAzureConnected, acquireAzureToken };
};

export { ADO_SCOPES };
